import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiAlertCircle } from "react-icons/fi";
import WebServiceAmazon from "../../../Services/WebServiceAmazon";
import { useSelector } from "react-redux";
import { ListSkeleton } from "../../Common/Skeleton/Skeleton";
// The tab strip is `.amz-tabs` from the wizard sheet, the same one the campaign
// pickers draw. Imported the way the other out-of-wizard callers import it
// (`AddProductsModal`, `SDAddTargetsDrawer`) — nothing in that file is global
// except two rules keyed on `.amz-wizard-host` / `.amz-wizard-open`, which this
// modal never sets.
import "../Campaigns/CreateCampaign/amazon-wizard.scss";
import useRovingFocus from "../Campaigns/CreateCampaign/shared/useRovingFocus";

/** One entry of the `asin` filter state. `title` is null for an ASIN that was
 *  typed in rather than ticked in the catalogue — see `mergeTyped`. */
export interface AsinSelection {
  asin: string;
  title?: string | null;
}

/**
 * `advertised` — my own product. Both tabs are drawn, but Enter list opens
 * first: pasting a known ASIN is the common case, and the catalogue is a long
 * scroll. Search is one click away for browsing.
 *
 * `targeted` — a competitor's product. `amz_products` holds MY catalogue, so it
 * would essentially never contain the ASIN being looked for; offering it is
 * worse than offering nothing. Enter list is the only tab, and the catalogue is
 * not even fetched.
 */
export type AsinModalMode = "advertised" | "targeted";

interface AsinModalProps {
  isOpen: boolean;
  selectedAsins?: AsinSelection[];
  onClose: () => void;
  onApply: (selected: AsinSelection[]) => void;
  /** Modal heading. Must match the dropdown entry that opened it. */
  title?: string;
  mode?: AsinModalMode;
}

type Tab = "search" | "list";

/** Plan §1: NOT /^B0/. Older and ISBN-derived ASINs are not B0-prefixed. */
const ASIN_PATTERN = /^[A-Z0-9]{10}$/i;

const normalise = (value: any) => String(value ?? "").trim().toUpperCase();

export const AsinModal: React.FC<AsinModalProps> = ({
  isOpen,
  onClose,
  onApply,
  selectedAsins = [],
  title = "Advertised ASIN",
  mode = "advertised",
}) => {
  const [selected, setSelected] = useState<AsinSelection[]>(selectedAsins);
  const [loader, setLoader] = useState(false);
  const [list, setList] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [typedText, setTypedText] = useState("");
  const [rejected, setRejected] = useState<string[]>([]);

  const showSearchTab = mode === "advertised";
  const [tab, setTab] = useState<Tab>("list");
  const uid = useId();

  const profileData =
    useSelector((state: any) => state.commonReducer.data) || null;
  const portfolioData =
    useSelector((state: any) => state.commonReducer.portfolio) || null;

  // One request in flight -- see the note on the same ref in CampaignModal.tsx.
  const listRequest = useRef<AbortController | null>(null);
  useEffect(() => () => listRequest.current?.abort(), []);

  // The catalogue is profile- and portfolio-scoped but NOT date-scoped, so
  // unlike CampaignModal this one does not read `commonReducer.range`.
  useEffect(() => {
    if (profileData && isOpen && showSearchTab) {
      getList();
    }
    // eslint-disable-next-line
  }, [profileData, portfolioData, isOpen, showSearchTab]);

  // Re-open with whatever the chip currently holds, so ticking rows and then
  // hitting Cancel does not leave those ticks behind for the next open.
  useEffect(() => {
    if (isOpen) {
      setSelected(selectedAsins);
      setSearchTerm("");
      setTypedText("");
      setRejected([]);
      setTab("list");
    }
    // eslint-disable-next-line
  }, [isOpen]);

  const term = searchTerm.toLowerCase();
  const filteredList = list?.filter(
    (item) =>
      item?.asin?.toLowerCase().includes(term) ||
      item?.sku?.toLowerCase().includes(term) ||
      item?.title?.toLowerCase().includes(term)
  );

  const getList = () => {
    if (profileData == null) {
      return false;
    }
    let body = {
      profileId: profileData.value,
      portfolioId: portfolioData?.value ?? "",
    };
    let qurey = new URLSearchParams(body).toString();
    setLoader(true);
    listRequest.current?.abort();
    const controller = new AbortController();
    listRequest.current = controller;
    WebServiceAmazon.getAPI({
      action: `amazon/get-products?` + qurey,
      body: null,
      signal: controller.signal,
    })
      .then((res: any) => {
        if (controller.signal.aborted) return;
        // This endpoint answers `{ success, list }` — not `data`, the way
        // `amazon/campaigns` does.
        const rows = (res?.list || []).filter((row: any) => row?.asin);
        setList(rows);
        setLoader(false);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setList([]);
        setLoader(false);
      });
  };

  const isSelected = (asin: any) =>
    selected?.some((item) => normalise(item.asin) === normalise(asin));

  const handleChange = (item: any) => {
    if (isSelected(item.asin)) {
      setSelected(
        selected.filter(
          (entry) => normalise(entry.asin) !== normalise(item.asin)
        )
      );
    } else {
      setSelected([
        ...selected,
        { asin: normalise(item.asin), title: item.title ?? null },
      ]);
    }
  };

  const handleRemove = (asin: string) =>
    setSelected(
      selected.filter((entry) => normalise(entry.asin) !== normalise(asin))
    );

  /** Decision 4. Accepts ASINs the catalogue does not have: one that matches
   *  nothing downstream simply returns no rows, which is correct.
   *
   *  Returns the merged list rather than only setting it, so Apply can flush a
   *  box the user typed into but never pressed Add on — see `handleApply`. */
  const mergeTyped = (text: string, into: AsinSelection[]) => {
    const tokens = text
      .split(/[\n,\s]+/)
      .map((token) => normalise(token))
      .filter((token) => token.length > 0);

    const bad = tokens.filter((token) => !ASIN_PATTERN.test(token));
    const good = tokens.filter((token) => ASIN_PATTERN.test(token));

    const next = [...into];
    good.forEach((asin) => {
      // De-duplicate against what is already selected, and against earlier
      // tokens in this same paste.
      if (next.some((entry) => normalise(entry.asin) === asin)) return;
      // A typed ASIN that IS in the catalogue ticks that row rather than
      // becoming a second entry — the row's `checked` matches on the
      // uppercased asin, so carrying the catalogue title over is enough.
      const match = list.find((row: any) => normalise(row.asin) === asin);
      next.push({ asin, title: match?.title ?? null });
    });

    return { next, bad: Array.from(new Set(bad)) };
  };

  const handleAddTyped = () => {
    const { next, bad } = mergeTyped(typedText, selected);
    setSelected(next);
    setRejected(bad);
    setTypedText("");
  };

  const handleApply = () => {
    // ⚠️ Typing into the box and pressing Apply without pressing Add used to
    // throw the typed ASINs away silently. Flush them here instead. Rejected
    // tokens hold the modal open on the Enter list tab so the message is seen.
    if (typedText.trim().length > 0) {
      const { next, bad } = mergeTyped(typedText, selected);
      setSelected(next);
      setRejected(bad);
      setTypedText("");
      if (bad.length > 0) {
        setTab("list");
        return;
      }
      onApply(next);
      onClose();
      return;
    }
    onApply(selected);
    onClose();
  };

  const tabs = [
    ["list", "Enter list"],
    ...(showSearchTab ? ([["search", "Search"]] as [Tab, string][]) : []),
  ] as [Tab, string][];

  const openTab = (key: Tab) => {
    setTab(key);
    setRejected([]);
  };

  const tabKeys = useRovingFocus({
    count: tabs.length,
    activeIndex: tabs.findIndex(([key]) => key === tab),
    onActivate: (index) => openTab(tabs[index][0]),
    orientation: "horizontal",
  });

  if (!isOpen) return null;

  return createPortal(
    <div className="modal fade show d-block" tabIndex={-1} role="dialog">
      <div className="modal-backdrop fade show" onClick={onClose}></div>
      <div
        className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
        role="document"
        style={{ zIndex: 1050 }}
      >
        <div className="modal-content shadow">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>

          {/* The body is a column, not a scroller: the tab strip and the
              selection summary stay put and only the panel between them
              scrolls. Inline because `.modal-dialog-scrollable` puts
              `overflow-y: auto` on `.modal-body` itself, which is what buried
              the typed-ASIN box under the whole catalogue. */}
          <div
            className="modal-body d-flex flex-column p-0"
            style={{ overflowY: "hidden", minHeight: 0 }}
          >
            {tabs.length > 1 && (
              <div
                className="amz-tabs flex-shrink-0"
                role="tablist"
                aria-label="How to choose ASINs"
                {...tabKeys.containerProps}
              >
                {tabs.map(([key, label], index) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    id={`${uid}-tab-${key}`}
                    aria-selected={tab === key}
                    aria-controls={`${uid}-tabpanel`}
                    {...tabKeys.itemProps(index)}
                    className={`amz-tabs__tab ${
                      tab === key ? "amz-tabs__tab--on" : ""
                    }`}
                    onClick={() => openTab(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}

            <div
              className="d-flex flex-column flex-grow-1"
              style={{ minHeight: 0 }}
              {...(tabs.length > 1
                ? {
                    id: `${uid}-tabpanel`,
                    role: "tabpanel",
                    "aria-labelledby": `${uid}-tab-${tab}`,
                  }
                : {})}
            >
              {tab === "search" ? (
                <>
                  {/* Above the scroller, not inside it: a search box that
                      scrolls away is the same defect as a buried text box. */}
                  <div className="flex-shrink-0 px-3 pt-3 pb-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by ASIN, SKU or title..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div
                    className="flex-grow-1 px-3 pb-3"
                    style={{ overflowY: "auto", minHeight: 240 }}
                  >
                    {loader ? (
                      <ListSkeleton checkbox thumbnail lines={1} rows={6} />
                    ) : filteredList?.length ? (
                      filteredList.map((item, index) => (
                        <div
                          className="form-check d-flex align-items-center mb-2"
                          key={index}
                        >
                          <input
                            className="form-check-input cursor-pointer flex-shrink-0"
                            type="checkbox"
                            id={`asin-filter-${item.asin}-${index}`}
                            checked={isSelected(item.asin)}
                            onChange={() => handleChange(item)}
                          />
                          <label
                            className="form-check-label cursor-pointer d-flex align-items-center ms-2"
                            htmlFor={`asin-filter-${item.asin}-${index}`}
                            style={{ minWidth: 0 }}
                          >
                            {item.product_image ? (
                              <img
                                src={item.product_image}
                                alt=""
                                width={32}
                                height={32}
                                className="flex-shrink-0 me-2"
                                style={{ objectFit: "contain" }}
                              />
                            ) : (
                              <span
                                className="flex-shrink-0 me-2 bg-light d-inline-block"
                                style={{ width: 32, height: 32 }}
                              />
                            )}
                            <span className="fw-semibold me-2">{item.asin}</span>
                            <span
                              className="text-muted text-truncate"
                              style={{ maxWidth: "420px" }}
                              title={item.title || ""}
                            >
                              {item.title}
                            </span>
                          </label>
                        </div>
                      ))
                    ) : (
                      <div className="text-muted py-4 text-center">
                        {list.length === 0
                          ? "Your products could not be loaded."
                          : "No products match that search."}{" "}
                        Use <b>Enter list</b> to type ASINs.
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div
                  className="flex-grow-1 p-3"
                  style={{ overflowY: "auto", minHeight: 240 }}
                >
                  <label className="form-label" htmlFor={`${uid}-typed`}>
                    {mode === "targeted"
                      ? "Enter the ASINs your campaigns target."
                      : "Enter the ASINs your campaigns advertise."}
                  </label>
                  <textarea
                    id={`${uid}-typed`}
                    className="form-control"
                    rows={6}
                    placeholder="Enter ASINs separated by a new line or comma."
                    value={typedText}
                    onChange={(e) => setTypedText(e.target.value)}
                  />
                  <button
                    className="btn btn-outline-secondary mt-2"
                    onClick={handleAddTyped}
                    disabled={typedText.trim().length === 0}
                  >
                    Add
                  </button>
                  {/* `.amz-inline-error` + FiAlertCircle is the wizard's own
                      error row (`ProductPicker`, `KeywordListInput`), and the
                      wording matches `ProductPicker`'s paste feedback: the bad
                      tokens were SKIPPED, the good ones on the same paste were
                      added, and saying so is the difference between a message
                      and a dead end. */}
                  {rejected.length > 0 && (
                    <div className="amz-inline-error mt-2" role="alert">
                      <FiAlertCircle size={15} className="flex-shrink-0 mt-1" />
                      <span style={{ wordBreak: "break-word" }}>
                        {rejected.length} skipped — not a valid ASIN:{" "}
                        {rejected.join(", ")}. An ASIN is exactly 10 letters or
                        digits.
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Outside the tabs on purpose: switching tabs must not look like
                it dropped what was already picked. The COUNT moved down into
                the footer beside the buttons; only the chips live here, so
                nothing is selected there is no empty bordered strip at all. */}
            {selected.length > 0 && (
              <div className="flex-shrink-0 border-top px-3 py-2">
                <div
                  className="d-flex flex-wrap gap-1"
                  style={{ maxHeight: 84, overflowY: "auto" }}
                >
                  {selected.map((entry) => (
                    <span
                      key={entry.asin}
                      className="badge bg-light text-dark border d-inline-flex align-items-center"
                      title={entry.title || ""}
                    >
                      {entry.asin}
                      <button
                        type="button"
                        className="btn-close ms-1"
                        style={{ fontSize: "0.5rem" }}
                        aria-label={`Remove ${entry.asin}`}
                        onClick={() => handleRemove(entry.asin)}
                      />
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* `justify-content-between`: Bootstrap's own `.modal-footer` packs
              everything to the right, so the count needs the override to sit
              opposite Cancel / Apply. `role="status"` because the number is
              the only thing that moves when a row is ticked. */}
          <div className="modal-footer justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted" role="status">
                {selected.length} ASIN{selected.length === 1 ? "" : "s"} selected
              </span>
              {selected.length > 0 && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  onClick={() => setSelected([])}
                >
                  Clear all
                </button>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              <button className="btn btn-outline-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                style={{ height: "38px" }}
                className="btn btn-primary"
                onClick={handleApply}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
